import {publishEvent, TrackImpression} from '@udemy-v2/event-tracking';
import {useMatchMedia} from '@udemy-v2/design-system-utils';
import {useI18n} from '@udemy-v2/i18n';
import {Image} from '@udemy-v2/react-core-components';
import classNames from 'classnames';
import React from 'react';

import {
    CodingExercisePromotionImpressionEvent,
    CodingExercisePromotionImageClickEvent,
} from './events';
import {CodingExerciseModalTrigger} from './coding-exercise-promotion-modal.react-component';

import styles from './coding-exercise-promotion.module.scss';
import desktopPreviewImg from './assets/coding-exercises-demo-preview-desktop.png';
import mobilePreviewImg from './assets/coding-exercises-demo-preview-mobile.png';

export const enum CodingExercisePromotionLayouts {
    VERTICAL = 'VERTICAL',
    HORIZONTAL = 'HORIZONTAL',
}

export interface CodingExercisePromotionProps {
    courseId: number;
    frontendTrackingId: string;
    layout?: CodingExercisePromotionLayouts;
}

export const CodingExercisePromotion = ({
    layout,
    courseId,
    frontendTrackingId,
}: CodingExercisePromotionProps) => {
    const {gettext} = useI18n();
    const isMobileMax = useMatchMedia('sm-max');
    const previewImage = isMobileMax ? mobilePreviewImg : desktopPreviewImg;
    const trackingId = frontendTrackingId;

    const resolvedLayout =
        layout ??
        (isMobileMax
            ? CodingExercisePromotionLayouts.VERTICAL
            : CodingExercisePromotionLayouts.HORIZONTAL);

    const containerProps = {
        className: classNames(styles['coding-exercise-container'], {
            [styles['coding-exercise-container-horizontal']]:
                resolvedLayout === CodingExercisePromotionLayouts.HORIZONTAL,
        }),
    };

    const handleImpression = () => {
        publishEvent(
            new CodingExercisePromotionImpressionEvent({
                id: courseId,
                trackingId: trackingId,
            }),
        );
    };

    return (
        <TrackImpression trackFunc={handleImpression}>
            <div {...containerProps}>
                <div className={styles['coding-exercise-content']}>
                    <h2 className="ud-heading-xl">{gettext('Coding Exercises')}</h2>
                    <p className={classNames('ud-text-sm', styles['coding-exercise-description'])}>
                        {gettext(
                            'This course includes our updated coding exercises so you can practice your skills as you learn.',
                        )}
                    </p>
                    <CodingExerciseModalTrigger
                        assetTitle={gettext('Coding Exercise Demo')}
                        assetId={48073474}
                        courseId={courseId}
                        ctaText={gettext('See a demo')}
                        trackingId={trackingId}
                    />
                </div>
                <div className={styles['coding-exercise-image-container']}>
                    <Image
                        className={styles['coding-exercise-image']}
                        role="button"
                        aria-label={gettext(
                            'This image is also treated as a button to track image enlargement interest',
                        )}
                        alt={gettext('Image of coding exercise example')}
                        src={previewImage.src}
                        width={previewImage.width}
                        height={previewImage.height}
                        onClick={() => {
                            publishEvent(
                                new CodingExercisePromotionImageClickEvent({
                                    id: courseId,
                                    trackingId: trackingId,
                                }),
                            );
                        }}
                    />
                </div>
            </div>
        </TrackImpression>
    );
};
